import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { orderApi } from '../lib/orderApi';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    // Auth is now fully handled by httpOnly cookies - no localStorage needed
    try {
      const { data } = await orderApi.get('/auth/me');
      setUser(data.user);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const login = useCallback(async (email, password) => {
    const { data } = await orderApi.post('/auth/login', { email, password });
    // Auth cookie is set by the server (httpOnly) - no localStorage storage
    setUser(data.user);
    return data;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await orderApi.post('/auth/register', payload);
    // Auth cookie is set by the server (httpOnly)
    setUser(data.user);
    return data.user;
  }, []);

  const googleLogin = useCallback(async (sessionId) => {
    const { data } = await orderApi.post('/auth/google/session', { session_id: sessionId });
    // For 2FA required (admin), don't set user yet - return data for callback to handle
    if (data.requires_2fa) {
      return data;
    }
    // Auth cookie is set by the server (httpOnly)
    setUser(data.user);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      // Call server to clear httpOnly cookie
      await orderApi.post('/auth/logout');
    } catch (e) {
      // Logout request failed (network/expired session) — clear local state anyway
      console.error('Logout request failed, clearing local state:', e);
    }
    setUser(false);
  }, []);

  // For 2FA flow - reload user after verification (cookie already set)
  const setToken = useCallback(async () => {
    await loadMe(); // Load user data from cookie
  }, [loadMe]);

  // Actions are all stable (useCallback), memoized separately from the
  // user/loading state so the state and actions groups can change independently.
  const actions = useMemo(
    () => ({ login, register, googleLogin, logout, refreshUser: loadMe, setToken }),
    [login, register, googleLogin, logout, loadMe, setToken]
  );

  const value = useMemo(
    () => ({ user, loading, ...actions }),
    [user, loading, actions]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
